import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { canEditOwn, useAuthStore } from "@/features/auth/store";
import {
  useCreateKnowledgeBase,
  useDeleteKnowledgeBase,
  useKnowledgeBases,
  useUpdateKnowledgeBase,
} from "@/features/knowledge-bases/hooks";
import type { KnowledgeBase } from "@/features/knowledge-bases/api";

export default function KnowledgeBasesPage() {
  const role = useAuthStore((s) => s.user?.role ?? "member");
  // Customers own everything they can see here, so rank does not apply.
  const canEdit = canEditOwn(role);

  const q = useKnowledgeBases();
  const items = q.data ?? [];

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<KnowledgeBase | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<KnowledgeBase | null>(null);

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Knowledge Bases
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage documents and information for your AI agents.
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" />
            Create Knowledge Base
          </Button>
        )}
      </div>

      {q.isLoading && <KnowledgeBaseGridSkeleton />}

      {q.isError && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-destructive">
            Failed to load knowledge bases.
          </CardContent>
        </Card>
      )}

      {!q.isLoading && !q.isError && items.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <BookOpen className="size-10 text-muted-foreground" />
            <div>
              <p className="text-base font-medium">No Knowledge Bases</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Create one to start providing information to your agents.
              </p>
            </div>
            {canEdit && (
              <Button onClick={() => setCreateOpen(true)} className="mt-2">
                <Plus className="size-4" />
                Create Knowledge Base
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {!q.isLoading && items.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((kb) => (
            <KnowledgeBaseCard
              key={kb.id}
              kb={kb}
              canEdit={canEdit}
              onEdit={() => setEditTarget(kb)}
              onDelete={() => setDeleteTarget(kb)}
            />
          ))}
        </div>
      )}

      <CreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      <EditDialog
        target={editTarget}
        onClose={() => setEditTarget(null)}
      />
      <DeleteConfirm
        target={deleteTarget}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}

function KnowledgeBaseCard({
  kb,
  canEdit,
  onEdit,
  onDelete,
}: {
  kb: KnowledgeBase;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <Card className="group relative overflow-hidden transition-shadow hover:shadow-md">
      <Link
        to={`/knowledge-bases/${kb.id}`}
        className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <CardHeader className="flex-row items-center gap-2.5 space-y-0">
          <div className="flex size-9 items-center justify-center rounded-md bg-indigo-100 text-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-400">
            <BookOpen className="size-5" />
          </div>
          <CardTitle className="truncate text-base">{kb.name}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="line-clamp-2 min-h-[2.5em] text-sm text-muted-foreground">
            {kb.description || "No description"}
          </p>
          <div className="flex flex-wrap gap-1.5">
            <Chip>{kb.document_count} documents</Chip>
            <Chip>{kb.chunk_count} chunks</Chip>
          </div>
        </CardContent>
      </Link>

      {canEdit && (
        <div className="flex justify-end gap-1 border-t px-3 py-2">
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            aria-label={`Edit ${kb.name}`}
          >
            <Pencil className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-8 text-destructive hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            aria-label={`Delete ${kb.name}`}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      )}
    </Card>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-input bg-background px-2 py-0.5 text-xs text-muted-foreground">
      {children}
    </span>
  );
}

function KnowledgeBaseGridSkeleton() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <Card key={i}>
          <CardHeader className="flex-row items-center gap-2.5 space-y-0">
            <Skeleton className="size-9 rounded-md" />
            <Skeleton className="h-5 w-32" />
          </CardHeader>
          <CardContent className="space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
            <div className="flex gap-1.5">
              <Skeleton className="h-5 w-20 rounded-full" />
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function CreateDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const create = useCreateKnowledgeBase();

  const reset = () => {
    setName("");
    setDescription("");
  };

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await create.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
      });
      toast.success("Knowledge base created");
      reset();
      onOpenChange(false);
    } catch {
      toast.error("Failed to create knowledge base");
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create Knowledge Base</DialogTitle>
          <DialogDescription>
            Group documents your agents can read from.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="kb-name">Name</Label>
            <Input
              id="kb-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="kb-desc">Description</Label>
            <Textarea
              id="kb-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending || !name.trim()}>
              {create.isPending ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({
  target,
  onClose,
}: {
  target: KnowledgeBase | null;
  onClose: () => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const update = useUpdateKnowledgeBase();

  useEffect(() => {
    if (target) {
      setName(target.name);
      setDescription(target.description ?? "");
    }
  }, [target]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!target || !name.trim()) return;
    try {
      await update.mutateAsync({
        id: target.id,
        data: {
          name: name.trim(),
          description: description.trim() || undefined,
        },
      });
      toast.success("Knowledge base updated");
      onClose();
    } catch {
      toast.error("Failed to update knowledge base");
    }
  }

  return (
    <Dialog
      open={!!target}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Knowledge Base</DialogTitle>
          <DialogDescription>
            Update the name or description.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="kb-edit-name">Name</Label>
            <Input
              id="kb-edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="kb-edit-desc">Description</Label>
            <Textarea
              id="kb-edit-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending || !name.trim()}>
              {update.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteConfirm({
  target,
  onClose,
}: {
  target: KnowledgeBase | null;
  onClose: () => void;
}) {
  const remove = useDeleteKnowledgeBase();

  async function confirm() {
    if (!target) return;
    try {
      await remove.mutateAsync(target.id);
      toast.success("Knowledge base deleted");
      onClose();
    } catch {
      toast.error("Failed to delete knowledge base");
    }
  }

  return (
    <AlertDialog
      open={!!target}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete knowledge base</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete{" "}
            <span className="font-medium text-foreground">{target?.name}</span>?
            This also removes all of its documents and chunks. This cannot be
            undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending}
            onClick={(e) => {
              e.preventDefault();
              confirm();
            }}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {remove.isPending ? "Deleting…" : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
